import { type ComponentType, type ReactNode } from "react";
import { cn } from "@strivelabs/ui-core";
import { FileText, Image as ImageIcon, Paperclip, Send, Square } from "lucide-react";
import { useComposer } from "./context";
import { IconButton, Popover, Tooltip } from "@strivelabs/ui-core/v2";

/** The composer's bottom action row. */
export const ComposerToolbar = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("flex items-center gap-1 p-2", className)}>{children}</div>
);

/** Thin vertical divider between toolbar groups. */
export const ComposerDivider = () => <div className="mx-1 h-5 w-px bg-border" />;

/** Pushes trailing controls (voice, send) to the right. */
export const ComposerSpacer = () => <div className="flex-1" />;

const MenuItem = ({
  icon: Icon,
  title,
  subtitle,
  onClick,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent"
  >
    <Icon className="size-4 shrink-0 text-muted-foreground" />
    <span className="min-w-0">
      <span className="block text-sm font-medium leading-tight">{title}</span>
      <span className="block text-xs text-muted-foreground">{subtitle}</span>
    </span>
  </button>
);

export interface ComposerAttachProps {
  onPickFiles?: () => void;
  onPickImages?: () => void;
  /** Subtitle for the Files row (the host's allowed document types). */
  filesHint?: string;
  /** Subtitle for the Images row (the host's allowed image types). */
  imagesHint?: string;
  className?: string;
}

/** Attach button with a Files / Images menu. */
export const ComposerAttach = ({
  onPickFiles,
  onPickImages,
  filesHint = "Documents",
  imagesHint = "Images",
  className,
}: ComposerAttachProps) => {
  const { openMenu, setOpenMenu, disabled } = useComposer();
  const open = openMenu === "attach";
  return (
    <Tooltip label="Attach files" shortcut="⌘U" disabled={open}>
      <Popover
      open={open}
      onOpenChange={(next) => setOpenMenu(next ? "attach" : null)}
      aria-label="Attach files or images"
      contentClassName="w-64 p-1.5"
      trigger={
        <IconButton
          aria-label="Attach files or images"
          aria-haspopup="menu"
          aria-expanded={open}
          active={open}
          disabled={disabled}
          className={className}
        >
          <Paperclip className="size-[17px]" />
        </IconButton>
      }
    >
      <MenuItem
        icon={FileText}
        title="Files"
        subtitle={filesHint}
        onClick={() => {
          onPickFiles?.();
          setOpenMenu(null);
        }}
      />
      <MenuItem
        icon={ImageIcon}
        title="Images"
        subtitle={imagesHint}
        onClick={() => {
          onPickImages?.();
          setOpenMenu(null);
        }}
      />
      </Popover>
    </Tooltip>
  );
};

export interface ComposerSendProps {
  onStop?: () => void;
  className?: string;
}

/** Send / Stop control. Submits the form; becomes Stop while busy. */
export const ComposerSend = ({ onStop, className }: ComposerSendProps) => {
  const { busy, canSubmit, disabled } = useComposer();

  if (busy) {
    return (
      <Tooltip label="Stop">
        <button
          type="button"
          onClick={onStop}
          aria-label="Stop generation"
          className={cn(
            "relative inline-flex size-9 items-center justify-center rounded-xl bg-[#1c1c1c] text-white transition-[background-color,transform] before:absolute before:-inset-0.5 before:content-[''] hover:bg-black active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            className,
          )}
        >
          <Square className="size-4 fill-current" />
        </button>
      </Tooltip>
    );
  }

  const enabled = canSubmit && !disabled;
  return (
    <Tooltip label="Send message" shortcut={enabled ? "↵" : undefined}>
      <button
        type="submit"
        disabled={!enabled}
        aria-label="Send message"
        className={cn(
          "relative inline-flex size-9 items-center justify-center rounded-xl transition-[background-color,transform] before:absolute before:-inset-0.5 before:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          enabled
            ? "bg-[#1c1c1c] text-white hover:bg-black active:scale-[0.96] shadow-sm"
            : "cursor-not-allowed bg-muted text-muted-foreground",
          className,
        )}
      >
        <Send className="size-[17px]" />
      </button>
    </Tooltip>
  );
};
